import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { generateClient } from "aws-amplify/data";
import { fetchAuthSession } from "aws-amplify/auth";

const client = generateClient();
const defaultNews = ["Bienvenidos a Educacion en la fe."];
const speedKey = "noticiasVelocidad";

async function pickAuthMode() {
  try {
    const session = await fetchAuthSession();
    return session.tokens ? "userPool" : "identityPool";
  } catch {
    return "identityPool";
  }
}

export default function NewsTicker() {
  const [news, setNews] = useState(defaultNews);
  const [speed, setSpeed] = useState(1);
  const [sequenceCopies, setSequenceCopies] = useState(1);
  const [duration, setDuration] = useState(32);
  const viewportRef = useRef(null);
  const sequenceRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    const loadNews = async () => {
      try {
        const authMode = await pickAuthMode();
        const [newsResult, settingsResult] = await Promise.all([
          client.models.Noticia.list({ authMode }),
          client.models.Parametro.list({
            authMode,
            filter: { clave: { eq: speedKey } },
          }),
        ]);
        const headlines = newsResult.data
          .filter((news) => news.activa)
          .sort(
            (first, second) =>
              second.peso - first.peso ||
              new Date(first.createdAt) - new Date(second.createdAt),
          )
          .map((news) => news.mensaje);

        if (isMounted) setNews(headlines.length ? headlines : defaultNews);
        const configuredSpeed = Number(settingsResult.data[0]?.valor);
        if (isMounted && configuredSpeed >= 1 && configuredSpeed <= 10) {
          setSpeed(configuredSpeed);
        }
      } catch (error) {
        console.error("Error al obtener las noticias:", error);
      }
    };

    loadNews();
    window.addEventListener("news-updated", loadNews);
    const refreshInterval = window.setInterval(loadNews, 60000);

    return () => {
      isMounted = false;
      window.removeEventListener("news-updated", loadNews);
      window.clearInterval(refreshInterval);
    };
  }, []);

  useLayoutEffect(() => {
    const measureTicker = () => {
      const viewportWidth = viewportRef.current?.clientWidth;
      const sequenceWidth = sequenceRef.current?.scrollWidth;
      if (!viewportWidth || !sequenceWidth) return;

      const requiredCopies = Math.max(
        1,
        Math.ceil((viewportWidth + 120) / (sequenceWidth / sequenceCopies)),
      );
      if (requiredCopies !== sequenceCopies) {
        setSequenceCopies(requiredCopies);
        return;
      }

      setDuration(Math.max(2, sequenceWidth / (80 * speed)));
    };

    measureTicker();
    window.addEventListener("resize", measureTicker);
    return () => window.removeEventListener("resize", measureTicker);
  }, [news, sequenceCopies, speed]);

  const headlines = Array.from(
    { length: news.length * sequenceCopies },
    (_, index) => news[index % news.length],
  );

  const renderHeadlines = () =>
    headlines.map((headline, index) => (
      <span className="news-ticker__headline" key={`${headline}-${index}`}>
        <span className="news-ticker__bullet" aria-hidden="true" />
        {headline}
      </span>
    ));

  return (
    <section className="news-ticker" aria-label="Noticias destacadas">
      <div className="news-ticker__label">Noticias</div>
      <div className="news-ticker__viewport" ref={viewportRef}>
        <div
          className="news-ticker__track"
          style={{ "--news-ticker-duration": `${duration}s` }}
        >
          <div className="news-ticker__sequence" ref={sequenceRef}>
            {renderHeadlines()}
          </div>
          <div className="news-ticker__sequence" aria-hidden="true">
            {renderHeadlines()}
          </div>
        </div>
      </div>
    </section>
  );
}
